import React, { PropsWithChildren } from 'react'
import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AuthRegisterRequest, AuthRegisterResponse, User } from '@knots/api-sdk'

import { knotsApiSdk, knotsCFApiSdk } from '@/lib/fetcher'
import tokenStorage, { tokenStorageKey } from './token.storage'

import { reactQuery } from '@/types'
import { KNOTS_PLATFORM_URL } from '@/config'
import { getOAuth2Token } from '@/lib/knots-api'
import { ErrorReporting, HubspotIdentifier, useSessionExpiryWatcher } from '@knots/shared-frontend'
import { useTranslation } from 'react-i18next'
import { toastError } from '@/utils/toast'

export const useUserData = (params: { config?: reactQuery.QueryConfig<typeof knotsApiSdk.getUserData> } = {}) => {
  const { config = {} } = params
  return useQuery<reactQuery.ExtractFnReturnType<typeof knotsApiSdk.getUserData>>({
    queryKey: ['userData'],
    queryFn: knotsApiSdk.getUserData,
    ...config,
  })
}

export type AuthProviderValues = {
  initialized: boolean
  user: null | User
  login: (accessToken: string, stayLoggedIn: boolean) => void
  isLoggedIn: () => boolean
  logout: () => Promise<void>
  register: (_: AuthRegisterRequest) => Promise<AuthRegisterResponse>
  changePassword: (password: string, newPassword: string) => Promise<void>
  openInKnots: UseMutationResult<void, unknown, string | void, unknown>
}

const defaultValues: AuthProviderValues = {
  initialized: false,
  user: null,
  isLoggedIn: () => {
    return false
  },
  login: () => {},
  logout: async () => {},
  register: async () => ({}),
  changePassword: async () => {},
  openInKnots: { mutate: (url?: string) => {}, isPending: false } as UseMutationResult<
    void,
    unknown,
    string | void,
    unknown
  >,
}

export const AuthContext = React.createContext(defaultValues)
export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient()
  // Seeded synchronously: the SSO exchange below runs in an effect, so without this
  // RequireAuth would render first, see no token, and redirect away before it starts
  const [isSSO, setIsSSO] = React.useState(() => new URLSearchParams(window.location.search).has('sso'))
  const tokenQuery = tokenStorage.useToken()
  const { t } = useTranslation()

  // Warns before the session runs out, so unsaved work can be saved in time.
  useSessionExpiryWatcher(tokenQuery?.data, tokenStorageKey, (minutesLeft) =>
    toastError(t('auth.sessionExpiry.title'), t('auth.sessionExpiry.description', { count: minutesLeft }), {
      autoClose: false,
    })
  )

  const userDataQuery = useUserData({
    config: {
      enabled: !!tokenQuery?.data,
    },
  })
  const user = userDataQuery.data ?? null

  // Check if SSO token present and login if needed
  React.useEffect(() => {
    const getRealToken = async () => {
      const urlSearchParams = new URLSearchParams(window.location.search)
      const params = Object.fromEntries(urlSearchParams.entries())
      if (params?.sso) {
        setIsSSO(true)
        tokenStorage.setToken(params?.sso, false)
        const ssoRes = await knotsApiSdk.loginWithSSOToken()
        if (ssoRes && ssoRes.access_token) {
          tokenStorage.setToken(ssoRes.access_token, true)
        }
        setIsSSO(false)

        // Drop the now-consumed sso token from the URL so it isn't left visible/bookmarkable
        const cleanParams = new URLSearchParams(window.location.search)
        cleanParams.delete('sso')
        const cleanSearch = cleanParams.toString()
        window.history.replaceState(
          null,
          '',
          window.location.pathname + (cleanSearch ? `?${cleanSearch}` : '') + window.location.hash,
        )
      }
    }

    getRealToken()
  }, [])

  const login = async (accessToken: string, stayLoggedIn = false) => {
    if (accessToken) {
      tokenStorage.setToken(accessToken, stayLoggedIn)
    }
  }

  const register = async (data: AuthRegisterRequest) => {
    return await knotsCFApiSdk.registerUser(data)
  }

  const logout = async () => {
    tokenStorage.clearToken()
    queryClient.clear()
    // window.location.assign(window.location.origin as unknown as string)
  }

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await knotsApiSdk.changePassword({ currentPassword, newPassword })
  }

  const isLoggedIn = () => {
    return !!tokenStorage.getToken()
  }

  // Mutation for getting SSO token and opening user profile
  const openInKnots = useMutation<void, unknown, string | void, unknown>({
    mutationFn: async (url = '/modules/all') => {
      if (!user) return
      const token = await getOAuth2Token()
      window.open(`${KNOTS_PLATFORM_URL}${url}?sso=${token.access_token}`, '_blank')
    },
  })

  const state = {
    user,
    initialized: (userDataQuery.isSuccess || (tokenQuery.isSuccess && userDataQuery.fetchStatus === 'idle')) && !isSSO,
    isLoggedIn,
    login,
    logout,
    register,
    changePassword,
    openInKnots,
  }

  return (
    <AuthContext.Provider value={state}>
      <HubspotIdentifier knotsApiSdk={knotsApiSdk} isReady={state.initialized} isLoggedIn={state.isLoggedIn()} email={user?.email}/>
      <ErrorReporting
        source="studio"
        userId={state.user?.id}
        teamId={state.user?.team?.id}
        teamName={state.user?.team?.name}
      />
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  return React.useContext(AuthContext)
}
